import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import AppFooter from '../components/AppFooter';
import {
  Shield, ArrowLeft, TrendingUp, AlertCircle, ChevronRight,
  Sparkles, BookOpen, BarChart3
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScoreSimulatorPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [simulation, setSimulation] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSimulation();
  }, []);

  const loadSimulation = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/simulator/score-impact`, {});
      setSimulation(data);
      // Select all by default
      const all = {};
      data.items?.forEach((item) => { all[item.index] = true; });
      setSelectedItems(all);
    } catch (e) {
      console.error('Failed to load simulation:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const toggleItem = (index) => {
    setSelectedItems(prev => {
      const next = { ...prev };
      if (next[index]) delete next[index];
      else next[index] = true;
      return next;
    });
  };

  // Calculate selected improvement
  const selectedImprovement = simulation?.items?.reduce((acc, item) => {
    if (selectedItems[item.index]) {
      return {
        min: acc.min + item.estimated_improvement_min,
        max: acc.max + item.estimated_improvement_max
      };
    }
    return acc;
  }, { min: 0, max: 0 }) || { min: 0, max: 0 };

  // Cap at realistic levels
  const cappedMin = Math.min(selectedImprovement.min, 150);
  const cappedMax = Math.min(selectedImprovement.max, 250);

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-testid="score-simulator-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-[#36454F]/70 hover:text-[#36454F]">Sign Out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <Link to="/dashboard" className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Back to Dashboard
        </Link>

        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">
            Credit Score Simulator
          </h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            See how removing negative items could impact your credit score
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        ) : !simulation?.items?.length ? (
          <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
            <CardContent className="p-12 text-center">
              <BarChart3 className="w-16 h-16 text-[#36454F]/20 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">No Items to Simulate</h3>
              <p className="text-[#36454F]/65 font-['Manrope'] mb-6 max-w-md mx-auto">
                Upload and analyze a credit report to see estimated score improvements.
              </p>
              <Link to="/upload">
                <Button className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm">
                  Upload Credit Report <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Score Projection */}
            <div className="lg:col-span-2 space-y-6">
              {/* Big Score Display */}
              <Card className="border-[#36454F]/15 bg-white animate-fade-in-up" data-testid="score-projection-card">
                <CardHeader>
                  <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-[#4CAF50]" strokeWidth={1.5} />
                    Estimated Improvement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <div className="inline-flex items-baseline">
                      <span className="text-5xl font-['Playfair_Display'] font-semibold text-[#4CAF50]">
                        +{cappedMin}
                      </span>
                      <span className="text-2xl text-[#36454F]/40 mx-2 font-['Manrope']">to</span>
                      <span className="text-5xl font-['Playfair_Display'] font-semibold text-[#4CAF50]">
                        +{cappedMax}
                      </span>
                    </div>
                    <p className="text-sm text-[#36454F]/50 font-['Manrope'] mt-2">
                      estimated points if {Object.keys(selectedItems).length} selected item(s) are removed
                    </p>
                  </div>

                  {/* Score Range Bars */}
                  <div className="space-y-4 mt-6">
                    <p className="text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope']">
                      Projected Score by Starting Range
                    </p>
                    {[
                      { label: 'Poor (500)', base: 500, color: '#D9534F' },
                      { label: 'Fair (620)', base: 620, color: '#E0BFB8' },
                      { label: 'Good (700)', base: 700, color: '#B76E79' }
                    ].map((range) => {
                      const projected = Math.min(850, range.base + cappedMax);
                      const pct = ((projected - 300) / 550) * 100;
                      const basePct = ((range.base - 300) / 550) * 100;
                      return (
                        <div key={range.label} className="space-y-1">
                          <div className="flex items-center justify-between text-xs font-['Manrope']">
                            <span className="text-[#36454F]/60">{range.label}</span>
                            <span className="font-semibold text-[#36454F]">
                              {range.base} → <span style={{ color: range.color }}>{projected}</span>
                            </span>
                          </div>
                          <div className="h-4 bg-[#36454F]/5 rounded-sm relative overflow-hidden">
                            <div
                              className="absolute inset-y-0 left-0 rounded-sm opacity-30"
                              style={{ width: `${basePct}%`, backgroundColor: range.color }}
                            />
                            <div
                              className="absolute inset-y-0 left-0 rounded-sm transition-all duration-500"
                              style={{ width: `${pct}%`, backgroundColor: range.color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Items List */}
              <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-100">
                <CardHeader>
                  <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F]">
                    Select Items to Simulate Removal
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {simulation.items.map((item) => (
                    <label
                      key={item.index}
                      className={`flex items-start space-x-3 p-4 rounded-sm border cursor-pointer transition-all duration-200 ${
                        selectedItems[item.index]
                          ? 'border-[#4CAF50]/40 bg-[#4CAF50]/5'
                          : 'border-[#36454F]/10 bg-[#F5F5F5]'
                      }`}
                      data-testid={`sim-item-${item.index}`}
                    >
                      <Checkbox
                        checked={!!selectedItems[item.index]}
                        onCheckedChange={() => toggleItem(item.index)}
                        className="mt-1 border-[#36454F]/30 data-[state=checked]:bg-[#4CAF50] data-[state=checked]:border-[#4CAF50]"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-['Manrope'] font-medium text-[#36454F]">{item.account_name}</span>
                          <span className="text-sm font-['Manrope'] font-semibold text-[#4CAF50]">
                            +{item.estimated_improvement_min}-{item.estimated_improvement_max} pts
                          </span>
                        </div>
                        <div className="flex gap-2 mt-1 text-xs text-[#36454F]/50 font-['Manrope']">
                          <span>{item.account_number}</span>
                          <span>•</span>
                          <span>{item.bureau}</span>
                          <span>•</span>
                          <span className="text-[#D9534F]">{item.issue_type}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-200 sticky top-24">
                <CardContent className="p-6 space-y-4">
                  <div className="text-center p-4 bg-[#F5F5F5] rounded-sm">
                    <span className="text-xs text-[#36454F]/50 font-['Manrope'] block mb-1">Items Selected</span>
                    <span className="text-3xl font-['Manrope'] font-semibold text-[#36454F]">
                      {Object.keys(selectedItems).length}
                    </span>
                    <span className="text-sm text-[#36454F]/50 font-['Manrope']"> of {simulation.total_items}</span>
                  </div>

                  <Link to="/upload">
                    <Button className="w-full bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="start-disputing-btn">
                      Start Disputing
                      <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                    </Button>
                  </Link>

                  <Link to="/debt-calculator">
                    <Button variant="outline" className="w-full border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm">
                      Debt Calculator
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Disclaimer */}
              <Card className="border-[#E0BFB8] bg-[#E0BFB8]/10 animate-fade-in-up animation-delay-200">
                <CardContent className="p-5">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <p className="text-xs text-[#36454F]/60 font-['Manrope'] leading-relaxed">
                      {simulation.disclaimer}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Verse */}
              <Card className="border-[#E0BFB8] bg-[#E0BFB8]/5 animate-fade-in-up animation-delay-300">
                <CardContent className="p-5">
                  <BookOpen className="w-5 h-5 text-[#B76E79] mb-2" strokeWidth={1.5} />
                  <p className="font-['Playfair_Display'] text-sm italic text-[#36454F]">
                    "{simulation.verse}"
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      <AppFooter />
    </div>
  );
}
